import {
  BroadcastManyQuery,
  Multilingual,
  Owner,
} from "gql/generated";
import { ViewResource } from "./components";
import { BroadcastStatus } from "apps/home/constants";
import type { FormResourceInfo } from "./hooks/useFormResourceStatuses";
import type { DefaultContact } from "./components/StorefrontOrdersIndex";
import type { UserOperator } from "widgets/booker/contexts/BookerContextProvider/BookerContext";
import { BroadcastModalLayout } from "./BroadcastModalLayout";
import { ProgressStepper } from "shared/components/molecules/ProgressStepper";
import { Button, Modal, Text } from "@ttc3k/trekker";
import { Flex } from "styled-system/jsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { getMultilingualString } from "shared/utils";
import { useTranslation } from "react-i18next";
import _ from "lodash";

type Resource = NonNullable<
  BroadcastManyQuery["broadcastMany"][0]["resources"][0]
>;

type ViewResourceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isPreview: boolean;
  resource: Resource | null;
  broadcastId: string;
  broadcastOwner?: Owner;
  broadcastName?: Multilingual;
  organizationName?: Multilingual;
  personId?: string;
  personName?: string;
  status: BroadcastStatus;
  formStatusMap?: Record<string, FormResourceInfo>;
  allResources?: BroadcastManyQuery["broadcastMany"][0]["resources"];
  onNavigate?: (resource: Resource) => void;
  defaultContact?: DefaultContact;
  userOperators?: UserOperator[];
  isAdmin?: boolean;
};

export const ViewResourceModal = ({
  isOpen,
  onClose,
  isPreview,
  resource,
  broadcastId,
  organizationName,
  personId,
  status,
  formStatusMap,
  personName,
  broadcastOwner,
  broadcastName,
  allResources,
  onNavigate,
  defaultContact,
  userOperators,
  isAdmin,
}: ViewResourceModalProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.BROADCAST",
  });

  const resources = useMemo(
    () => _.compact(allResources ?? []),
    [allResources],
  );

  const currentIndex = useMemo(
    () => resources.findIndex(r => r._id === resource?._id),
    [resources, resource?._id],
  );

  const steps = useMemo(
    () =>
      resources.map(r => ({
        label: getMultilingualString(r.title),
      })),
    [resources],
  );

  const hasMultiple = resources.length > 1;
  const currentStep = currentIndex + 1;

  // Dirty state tracking for unsaved form changes
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  const guardAction = useCallback(
    (action: () => void) => {
      if (isDirty) {
        pendingActionRef.current = action;
        setShowUnsavedDialog(true);
      } else {
        action();
      }
    },
    [isDirty],
  );

  const handleLeave = useCallback(() => {
    setShowUnsavedDialog(false);
    setIsDirty(false);
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setShowUnsavedDialog(false);
    pendingActionRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    guardAction(onClose);
  }, [guardAction, onClose]);

  const handleNavigate = useCallback(
    (res: Resource) => {
      guardAction(() => onNavigate?.(res));
    },
    [guardAction, onNavigate],
  );

  const navigationFooter = hasMultiple ? (
    <Flex
      alignItems={"center"}
      justifyContent={"center"}
      width={"full"}
      py={"200"}
      px={"500"}
      position={"sticky"}
      bottom={isPreview ? "44px" : "0"}
      zIndex={"10"}
      bg={"white"}
      borderTop={"1px solid"}
      borderColor={"border.light"}
    >
      <ProgressStepper
        currentStep={currentStep}
        steps={steps}
        isLoading={false}
        onPreviousClick={() =>
          currentIndex > 0 && handleNavigate(resources[currentIndex - 1])
        }
        onNextClick={() =>
          currentIndex < resources.length - 1 &&
          handleNavigate(resources[currentIndex + 1])
        }
        onJumpToStepClick={(step: number) => {
          const idx = step - 1;
          if (idx >= 0 && idx < resources.length) {
            handleNavigate(resources[idx]);
          }
        }}
        menuProps={{
          positioning: {
            placement: "top",
          },
        }}
      />
    </Flex>
  ) : undefined;

  return (
    <>
      <BroadcastModalLayout
        isOpen={isOpen}
        onClose={handleClose}
        isPreview={isPreview}
        footerContent={navigationFooter}
      >
        {resource && (
          <ViewResource
            resource={resource}
            organizationName={organizationName}
            personId={personId}
            isPreview={isPreview}
            broadcastId={broadcastId}
            status={status}
            submittedCount={formStatusMap?.[resource._id]?.submittedCount}
            pendingCount={formStatusMap?.[resource._id]?.pendingCount}
            draftCount={formStatusMap?.[resource._id]?.draftCount}
            rejectedCount={formStatusMap?.[resource._id]?.rejectedCount}
            personName={personName}
            broadcastOwner={broadcastOwner}
            broadcastName={broadcastName}
            onDirtyChange={handleDirtyChange}
            defaultContact={defaultContact}
            userOperators={userOperators}
            isAdmin={isAdmin}
          />
        )}
      </BroadcastModalLayout>

      {/* Unsaved changes confirmation dialog */}
      <Modal
        open={showUnsavedDialog}
        onOpenChange={() => setShowUnsavedDialog(false)}
        placement="centerPane"
        portal
      >
        <Flex
          flexDir="column"
          gap="300"
          p="400"
          bg="white"
          borderRadius="200"
          maxW="400px"
          mx="auto"
        >
          <Text visual="bodySemiBold" color="text.dark">
            {t("FORM_UNSAVED_TITLE")}
          </Text>
          <Text visual="bodyRegular" color="text.mid">
            {t("FORM_UNSAVED_MESSAGE")}
          </Text>
          <Flex gap="150" justifyContent="flex-end">
            <Button mode="light" onClick={handleCancel} type="button" size="sm">
              {t("FORM_UNSAVED_CANCEL")}
            </Button>
            <Button
              visual="destructive"
              onClick={handleLeave}
              type="button"
              size="sm"
            >
              {t("FORM_UNSAVED_LEAVE")}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
